/**
 * Bulk Import — job detail view.
 * Shows all operations with their status. Auto-refreshes every 10s while running.
 */
import { loadFailure } from '../save-failure-words';
import { formatDateTime, formatTime } from '../../../utils/format-date';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/pill';
import { importActionTone } from '../status-tone';
import { Button } from '../../../components/button';
import { ImportJobOperation } from '../../../api/admin/import/types';
import { useImportJob } from '../../../api/admin/import/hooks';
import { ImportStatusBadge } from './import-status-badge';
import { FormMessage } from '../../../components/form-field';
import { Card } from '../../../components/card';
import bulk from './bulk-import.module.css';
import { percentOf } from '../../../utils/percent';

interface Props {
  jobId: string;
  onBack: () => void;
}

export function BulkImportJobDetail({ jobId, onBack }: Props) {
  const { t } = useTranslation();
  // Hook stops polling once the job reaches a terminal status (completed,
  // failed, partial, aborted) — see `use-admin-import.ts` refetchInterval.
  const query = useImportJob(jobId);
  const job = query.data ?? null;
  const refreshing = query.isFetching && !query.isLoading;
  const error = query.isError ? loadFailure(t, query.error, 'importJob') : null;

  if (error) return <FormMessage title={error.title}>{error.detail}</FormMessage>;
  if (!job) return <p>{t('admin.bulkImportHistory.detail.loading')}</p>;

  const pct = percentOf(job.appliedOps + job.failedOps, job.totalOps);

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="m-0">{t('admin.bulkImportHistory.detail.title')} #{job.id.slice(0, 8)}</h3>
          <div className={`${bulk.bulkImportDetailMeta} tone-soft`}>
            {t('admin.bulkImportHistory.detail.startedAt')}: {formatDateTime(job.startedAt)}
            {' · '}{t('admin.bulkImport.submitting.adminLabel')}: {job.adminName}
          </div>
        </div>
        {refreshing && <span className={bulk.bulkImportRefreshing}>🔄 {t('admin.bulkImportHistory.refreshing')}</span>}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <ImportStatusBadge status={job.status} />
        <span className="text-14">
          {job.appliedOps}/{job.totalOps} {t('admin.bulkImportHistory.appliedOf', { applied: job.appliedOps, total: job.totalOps })}
          {job.failedOps > 0 && <> · <span className="tone-danger">{t('admin.bulkImportHistory.failedCount', { count: job.failedOps })}</span></>}
        </span>
      </div>

      {job.totalOps > 0 && (
        <div className={bulk.bulkImportProgressBar}>
          <div className={bulk.bulkImportProgressFill} style={{ width: `${pct}%` }} />
        </div>
      )}

      <h4 className="mt-5">{t('admin.bulkImportHistory.detail.opsTitle')}</h4>
      <Card padded={false} className="overflow-hidden">
        {job.operations.map((op) => <OpRow key={op.id} op={op} />)}
        {job.operations.length === 0 && <div className={`${bulk.bulkImportOpEmpty} tone-soft`}>—</div>}
      </Card>

      <div className="mt-5">
        <Button type="button" variant="gold" onClick={onBack}>
          {t('admin.bulkImportHistory.detail.backToList')}
        </Button>
      </div>
    </div>
  );
}

function opStatusIcon(status: string): string {
  switch (status) {
    case 'applied': return '✅';
    case 'failed':  return '❌';
    case 'running': return '⏳';
    case 'skipped': return '⏸';
    default:        return '·';
  }
}

function OpRow({ op }: { op: ImportJobOperation }) {
  return (
    <>
      <div className={bulk.bulkImportOpRow}>
        <span className={bulk.bulkImportOpStatusIcon}>{opStatusIcon(op.status)}</span>
        <span className="tone-soft">{op.scope}</span>
        <span><Pill tone={importActionTone(op.action)}>{op.action}</Pill></span>
        <span className="font-mono text-12">{op.identifier}</span>
        {op.appliedAt && <span className={`${bulk.bulkImportOpTime} tone-soft`}>{formatTime(op.appliedAt)}</span>}
      </div>
      {op.errorMessage && (
        <div className={bulk.bulkImportOpRow}>
          <div className={bulk.bulkImportOpError}>{op.errorMessage}</div>
        </div>
      )}
    </>
  );
}
